import { isTaxTreatment, type TaxTreatment } from './constants'

/** Count-aware headline phrasings. `other` carries a `{count}` placeholder. */
interface HeadlineCopy {
  zero: string
  one: string
  other: string
}

/**
 * Every string on the report's Next Steps card.
 *
 * A benefit gets two rendered lines, measured at Geist 14px against the 242px a bullet
 * has inside the 300px rail. Three lines a bullet fills most of a short viewport, and
 * this card has to stay seated in one.
 */
export interface NextStepsCopy {
  eyebrow: string
  headline: HeadlineCopy
  benefits: readonly string[]
  consultLabel: string
  plansLabel: string
}

/**
 * Picks the phrasing for the count. Kept out of the copy so a variant is three plain
 * strings — serializable, so the table can come from a flag variation or an API.
 */
export const renderHeadline = (
  headline: HeadlineCopy,
  attentionCount: number
): string => {
  if (attentionCount === 0) return headline.zero
  if (attentionCount === 1) return headline.one
  return headline.other.replace('{count}', String(attentionCount))
}

/**
 * One headline for all nine entity/count states. The entity signal lives in the benefits,
 * where it can name something concrete.
 */
const SHARED_HEADLINE: HeadlineCopy = {
  zero: 'You’re in good shape. Heard keeps it that way.',
  one: 'Heard handles your finances so you don’t have to manage this alone.',
  other:
    'Heard handles your finances so you don’t have to manage all {count} alone.',
}

export const DEFAULT_NEXT_STEPS_COPY: NextStepsCopy = {
  eyebrow: 'Next steps',
  headline: SHARED_HEADLINE,
  benefits: [
    'Done-for-you bookkeeping, so you stop missing deductions',
    // Not "year-round", which is the annual filing that `lite` excludes.
    'Quarterly taxes handled, so April is never a surprise',
    'Unlimited support from experts who work with private practices',
  ],
  consultLabel: 'Book a Free Consult',
  plansLabel: 'Start Using Heard Now',
}

/**
 * Per-entity overrides keyed on the `tax_treatment` answer. Partial so a variant states
 * only what differs, which is the benefits alone.
 *
 * A benefit may only promise what every tier includes, because the card is seen before a
 * plan is chosen: `TIER_CARD_COPY` in `plan-pricing/recommendedPlan.copy.ts` has `lite`
 * exclude annual filing, S corporation election and payroll, and `essential` exclude the
 * business return. No line may name a tax form either, since an `scorp` answer does not
 * establish full-year S corporation status.
 */
export const NEXT_STEPS_COPY: Partial<
  Record<TaxTreatment, Partial<NextStepsCopy>>
> = {
  scorp: {
    benefits: [
      'Books that keep business and personal spending apart, as an S corporation needs',
      // Deliberately names no return: the business return is excluded at two of three tiers.
      'One set of books, ready for whatever you end up filing',
      'Experts who can answer S corporation questions, like how to set your salary',
    ],
  },
  soleprop: {
    benefits: [
      'See every transaction sorted into therapy categories',
      'Know what you owe each quarter, from real numbers',
      'Experts who can tell you when an S corporation would save you money',
    ],
  },
}

/**
 * Copy for any entity without a variant, and for a missing or unrecognized answer: the
 * report renders this card while the intake answers are still loading. The default
 * promises only what every practice gets, so nothing is walked back once one resolves.
 */
export const resolveNextStepsCopy = (taxTreatment: unknown): NextStepsCopy => {
  const variant = isTaxTreatment(taxTreatment)
    ? NEXT_STEPS_COPY[taxTreatment]
    : undefined
  return { ...DEFAULT_NEXT_STEPS_COPY, ...variant }
}
